"use client";

import { useAuth } from "@/lib/contexts";
import { useUserOrg } from "@/lib/data/userOrgs";
import { useOrgUid } from "@/lib/hooks/useCurrentOrg";

/**
 * Organization-scoped authentication utilities
 *
 * These utilities work with the new multi-tenancy architecture where:
 * - Users are stored globally in the `users` collection
 * - User-org relationships are stored in the global `jUserOrgs` collection
 * - Roles are org-specific and stored in the jUserOrgs documents
 */

/**
 * Hook to get the current user's org-specific roles
 *
 * @returns Object with loading state and user roles for the current org
 */
export function useUserOrgRoles() {
  const { user } = useAuth();
  const orgUid = useOrgUid();

  // Use the existing useUserOrg hook instead of duplicating the logic
  const {
    data: userOrgData,
    loading,
    error,
  } = useUserOrg(user?.uid || "", orgUid || "");

  // Get the first (and should be only) relationship
  const userOrg = userOrgData?.[0];

  return {
    roles: userOrg?.roles || {},
    loading,
    error,
    hasRelationship: !!userOrg,
  };
}

/**
 * Check if the current user has admin access in the current org
 *
 * @returns boolean indicating admin access
 */
export function useIsAdmin(): boolean {
  const { user } = useAuth();
  const { roles, hasRelationship } = useUserOrgRoles();

  if (!user) return false;

  // Check if user is a super admin (from custom claims)
  // This persists across all authentication methods
  if (user.customClaims?.isSuperAdmin === true) {
    return true;
  }

  // If no relationship with org, can't be admin
  if (!hasRelationship) {
    return false;
  }

  return roles.admin === true;
}

/**
 * Check if the current user has developer access in the current org
 *
 * @returns boolean indicating developer access
 */
export function useIsDeveloper(): boolean {
  const { user } = useAuth();
  const { roles, hasRelationship } = useUserOrgRoles();

  if (!user) return false;

  // In development, all users are developers
  if (process.env.NODE_ENV === "development") {
    return true;
  }

  // If no relationship with org, can't be developer
  if (!hasRelationship) return false;

  return roles.developer === true;
}

/**
 * Check if the current user has communications access in the current org
 *
 * @returns boolean indicating communications access
 */
export function useIsCommunications(): boolean {
  const { user } = useAuth();
  const { roles, hasRelationship } = useUserOrgRoles();
  const isAdminUser = useIsAdmin();

  if (!user) return false;

  // Admins automatically have communications access
  if (isAdminUser) return true;

  // If no relationship with org, can't have communications access
  if (!hasRelationship) return false;

  return roles.communications === true;
}

/**
 * Check if the current user has access to the current org
 *
 * @returns boolean indicating org access
 */
export function useHasOrgAccess(): boolean {
  const { user } = useAuth();
  const { hasRelationship } = useUserOrgRoles();

  if (!user) return false;

  // Domain restriction removed - allow users from any domain

  // Super admins have access to all orgs
  if (user.customClaims?.isSuperAdmin === true) {
    return true;
  }

  // Must have a relationship with the current org
  return hasRelationship;
}
